import { useMissionControlDataFlowData } from '@stores/data.store';
import { useCallback, useState, useEffect, useRef } from 'react';
import PageStructure, { PageContent, PageContentHeader, PageContentInner, Pane, PaneContent } from './PageStructure.component';
import PliableLoader from '@components/loaders/PliableLoader.component';
import { AssistantRuntimeProvider, useLocalRuntime, useComposer, useThreadRuntime, makeAssistantToolUI } from '@assistant-ui/react';
 import { Thread } from '@components/assistant-ui/thread';
import { Modal, Button } from 'react-bootstrap';
import toast from '@services/toast.service';
import { useFeatureFlagValue } from '@services/featureFlags/featureFlag.service';

import "@assistant-ui/styles/index.css";
import "@assistant-ui/styles/markdown.css";
import { createCustomModelAdapter } from '@services/assitantRuntime';
import AnalysisRunner from '@components/analysis/AnalysisRunner.component';
import { MissionControlDataFlowNode, MissionControlDataResponse } from '@stores/data.store';
import { confirmRunBuild } from '@components/Inbox.component';
import { useComposerSend } from '@assistant-ui/react/dist/primitives/composer/ComposerSend';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from '@services/url.service';
import { AnalysisVisualizationConfig } from '@models/analysis';
import { DashboardIcon } from '@components/pipelineNodes/PipelineNodeIcon.component';

// const ChartUI = makeAssistantToolUI<{ prompt: string }, { message: string, status: string }>({
//   toolName: 'generate_and_run_analysis',
//   render: (renderArgs) => {
//     console.log('Rendering chartui', renderArgs);
//     return <div>chart</div>
   
//   }
// });


type AgentType = 'build_chart' | 'analyst';

const ChartAssistantPage = () => {
    const missionControl = useMissionControlDataFlowData();
    const [assistantMode, setAssistantMode] = useState<AgentType>('build_chart');
    const modelAdapter = createCustomModelAdapter({ agent_type: assistantMode });
    const runtime = useLocalRuntime(modelAdapter);
    // Add explicit type annotation to help TypeScript
    const typedRuntime = runtime as any; // Type assertion to resolve type issues
    const enableEllieModes = useFeatureFlagValue('enable-ellie-modes', false);

    const resetThread = () => {
        // or
        typedRuntime.reset();
        // or
        typedRuntime.thread.messages = [];
    };

    const [loading, setLoading] = useState(false);
    const [resultSql, setResultSql] = useState('');
    const [error, setError] = useState('');

    // Check if there are nodes that need building with explicit type safety
    const missionData = missionControl.data as MissionControlDataResponse | undefined;
    const nodesThatNeedBuilding: MissionControlDataFlowNode[] = (missionData?.nodes || []).filter((node: MissionControlDataFlowNode) => node.data.needsBuilding === true);
    const hasUnbuildNodes: boolean = nodesThatNeedBuilding.length > 0;

    // State to track if nodes list is expanded
    const [nodesListExpanded, setNodesListExpanded] = useState(false);

    
    // State to control modal visibility
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [showModeModal, setShowModeModal] = useState(false);



    const changeAssistantMode = useCallback((newMode: AgentType) => {
        setAssistantMode(newMode);
        setAnalysisIds([]);
        setAnalysisVisualizationConfigs({});
        setActiveAnalysisId('');
        setShowModeModal(false);

        resetThread();
    }, []);

    

    const modeConfig = {
        build_chart: {
            label: 'Chart Builder',
            icon: 'mdi-chart-bar',
            description: 'Build custom charts and visualizations from your data with interactive guidance.'
        },
        analyst: {
            label: 'Analyst',
            icon: 'mdi-brain',
            description: 'Ask questions and get insights about your data through natural conversation. Note: this will consume more Snowflake credits.'
        }
    };

    

    const [analysisVisualizationConfigs, setAnalysisVisualizationConfigs] = useState<Record<string, AnalysisVisualizationConfig>>({});

    const [analysisIds, setAnalysisIds] = useState<string[]>([]);
    
    // Grab the initialQuestion query param and set it to a state variable called initialQuestion

    const [initialQuestion, setInitialQuestion] = useState('');
    

    const [activeAnalysisId, setActiveAnalysisId] = useState('');
    const sideContainerRef = useRef<HTMLDivElement>(null);
    const activeElementRef = useRef<HTMLDivElement>(null);


    const handleToolCall = useCallback((toolCall: any) => {
        if (toolCall.toolName === 'generate_and_run_analysis' && toolCall.status === 'complete' && toolCall.result) {
                // Add the analysis ID to the list if it's not already there.
                const analysisId = toolCall.result.analysis_id;
                if (!analysisIds.includes(analysisId)) {
                    setAnalysisIds([...analysisIds, analysisId]);
                    setActiveAnalysisId(analysisId);
                }
                setAnalysisVisualizationConfigs(prev => ({ ...prev, [analysisId]: toolCall.result.visualization_data }));
        }
        if (toolCall.toolName === 'generate_chart_input' && toolCall.status === 'complete' && toolCall.result) {
            // Add the analysis ID to the list if it's not already there.
            const analysisId = toolCall.result.analysis_id;
            if (!analysisIds.includes(analysisId)) {
                setAnalysisIds([...analysisIds, analysisId]);
                setActiveAnalysisId(analysisId);
            }
    }
    }, [analysisIds]);

    const updateInitialQuestion = useCallback(() => {
        setInitialQuestion(queryParams.get('initialQuestion') || '');
    }, []);
    
    // Auto-open modal when unbuilt nodes are detected
    useEffect(() => {
        if (hasUnbuildNodes && !missionControl.isLoading) {
            setShowWarningModal(true);
        } else {
            updateInitialQuestion();
        }
    }, [hasUnbuildNodes, missionControl.isLoading]);

    // Show toast notification for mission control errors
    useEffect(() => {
        if (missionControl.error) {
            toast('danger', 'Error', 'Failed to check node build status. Please refresh the page.');
        }
    }, [missionControl.error]);

    useEffect(() => {
        if (activeElementRef && activeElementRef.current && sideContainerRef.current) {
                const elementTop = activeElementRef.current?.offsetTop;

                // delay to ensure rendering is complete before scrolling
                setTimeout(() => {
                    sideContainerRef.current?.scrollTo({
                        top: elementTop,
                        behavior: 'smooth'
                    });
                }, 1000);
        }
    }, [activeAnalysisId]);

    const handleCloseModal = () => {
        setShowWarningModal(false);
        updateInitialQuestion();
    };

    const queryParams = useQueryParams();

 

    const handleRefreshData = async () => {
        setShowWarningModal(false);
        await confirmRunBuild(() => {
            updateInitialQuestion();
        });
    };

    
    typedRuntime.thread.subscribe(() => {
        const msgs = typedRuntime.thread.getState().messages;
        if (msgs.length === 0) {
            return;
        }
        const lastMessage = msgs[msgs.length - 1];
        if (lastMessage.content.length > 0 && lastMessage.content[lastMessage.content.length - 1].type === 'tool-call') {
            handleToolCall(lastMessage.content[lastMessage.content.length - 1]);
        }
    });

    return <PageStructure pageTitle="Data Assistant">
        <PageContent>
            {/* Warning Modal for unbuilt nodes */}
            <Modal show={showWarningModal} onHide={handleCloseModal} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="mdi mdi-alert-circle text-warning me-2"></i>
                        Data Refresh Required
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-3">
                        There {nodesThatNeedBuilding.length === 1 ? 'is' : 'are'} <strong>{nodesThatNeedBuilding.length}</strong> node{nodesThatNeedBuilding.length === 1 ? '' : 's'} that need{nodesThatNeedBuilding.length === 1 ? 's' : ''} building before you can use the AI Assistant.
                    </p>
                    
                    <div className="mb-3">
                        <strong>Nodes requiring build:</strong>
                        <ul className="mt-2 mb-0">
                            {nodesThatNeedBuilding.slice(0, 5).map(node => (
                                <li key={node.id}>{node.data.title}</li>
                            ))}
                            {nodesThatNeedBuilding.length > 5 && !nodesListExpanded && (
                                <li>
                                    <button
                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                        onClick={() => setNodesListExpanded(true)}
                                        style={{ color: 'var(--ct-primary)' }}
                                    >
                                        Show {nodesThatNeedBuilding.length - 5} more...
                                    </button>
                                </li>
                            )}
                            {nodesThatNeedBuilding.length > 5 && nodesListExpanded && (
                                <>
                                    {nodesThatNeedBuilding.slice(5).map(node => (
                                        <li key={node.id}>{node.data.title}</li>
                                    ))}
                                    <li>
                                        <button
                                            className="btn btn-link btn-sm p-0 text-decoration-none"
                                            onClick={() => setNodesListExpanded(false)}
                                            style={{ color: 'var(--ct-primary)' }}
                                        >
                                            Show less
                                        </button>
                                    </li>
                                </>
                            )}
                        </ul>
                    </div>
                    
                    <p className="text-muted mb-0">
                        Please refresh your data to ensure the AI Assistant works with the latest information.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-light me-1" onClick={handleCloseModal}>Close</button>
                    <button className="btn btn-pliable" onClick={handleRefreshData}>
                        <i className="mdi mdi-refresh me-2"></i>
                        Refresh Data
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Mode Selection Modal */}
            <Modal show={showModeModal} onHide={() => setShowModeModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select Agent Mode</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-3">
                        {(Object.keys(modeConfig) as AgentType[]).map((mode) => {
                            const config = modeConfig[mode];
                            const isActive = assistantMode === mode;
                            return (
                                <div key={mode} className="col-12">
                                    <div 
                                        className={`card h-100 ${isActive ? 'border-primary' : ''}`}
                                        onClick={() => changeAssistantMode(mode)}
                                        style={{ 
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            borderWidth: isActive ? '2px' : '1px'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.borderColor = '#dee2e6';
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.borderColor = '';
                                                e.currentTarget.style.boxShadow = '';
                                            }
                                        }}
                                    >
                                        <div className="card-body">
                                            <div className="d-flex align-items-start">
                                                <div className="me-3">
                                                    <i 
                                                        className={`mdi ${config.icon}`}
                                                        style={{ 
                                                            fontSize: '2.5rem',
                                                            color: isActive ? 'var(--ct-primary)' : '#6c757d'
                                                        }}
                                                    ></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h5 className="mb-2">
                                                        {config.label}
                                                        {isActive && (
                                                            <i className="mdi mdi-check-circle text-primary ms-2"></i>
                                                        )}
                                                    </h5>
                                                    <p className="text-muted mb-0">{config.description}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>
            </Modal>

            <PageContentHeader>
                <div className="d-flex center-vertically">
                    <DashboardIcon
                        icon="mdi mdi-robot"
                        bgColor="pliable"
                    />
                    <div>
                        <h1 className="mb-0">Ask Ellie</h1>
                    </div>
                    <div className="flex-1"></div>
                    {enableEllieModes && (
                        <div 
                            onClick={() => setShowModeModal(true)}
                            style={{ cursor: 'pointer' }}
                            className="d-flex align-items-center"
                        >
                            <i className={`mdi ${modeConfig[assistantMode].icon} me-2`} style={{ fontSize: '1.2rem' }}></i>
                            <div>
                                <span className="font-13 text-muted">Agent Mode: </span>
                                <span className="fw-bold">{modeConfig[assistantMode].label}</span>
                            </div>
                            <i className="mdi mdi-chevron-down ms-2"></i>
                        </div>
                    )}
                </div>
            </PageContentHeader>
            <PageContentInner hasHeader>
                <Pane>
                    <PaneContent>
                    <div className="row h-100">
                <div className="col-6 h-100">
                    <Pane>
                        <PaneContent >
                            
                            {loading && <PliableLoader />}
                            {error && <div className="alert alert-danger">{error}</div>}
                            {resultSql && <div className="alert alert-success">{resultSql}</div>}
                            {!loading && <>
                            <AssistantRuntimeProvider runtime={typedRuntime}>
                                
                                <Thread initialQuestion={initialQuestion} />
                                {/* <ChartUI/> */}

                            </AssistantRuntimeProvider>
                            </>}
                            
                        </PaneContent>
                    </Pane>
                </div>
                <div className="col-6 h-100 bg-light border-start">
                    <Pane>
                        <PaneContent ref={sideContainerRef}>
                            <div className="p-3 h-100">
                                <div className="list-group h-100">
                                    {analysisIds.length === 0 && <div className="text-muted">No charts generated yet. Ask Ellie to build one for you and you can see it here.</div>}
                                    {analysisIds.map(analysisId => {
                                        const isActive = activeAnalysisId === analysisId;
                                        return <div 
                                                key={analysisId}
                                                ref={isActive ? activeElementRef : null} 
                                                className="list-group-item d-flex flex-column"><AnalysisRunner visualizationConfig={analysisVisualizationConfigs[analysisId]} key={analysisId} autoRun analysisId={analysisId} compact={activeAnalysisId !== analysisId} onExpand={() => {
                                            setActiveAnalysisId(analysisId);
                                        }} onCollapse={() => {
                                            setActiveAnalysisId('');
                                        }} /></div>
                                    })}
                                </div>
                                
                            </div>
                        </PaneContent>
                    </Pane>
                </div>
            </div>
                    </PaneContent>

                </Pane>
            
            </PageContentInner>

            
        </PageContent>
    </PageStructure>
}

export default ChartAssistantPage;
